import React, { createContext, useLayoutEffect, useMemo, useState } from "react"

import { Res } from "./src/utils/constants"

const DEVICE = {
  mobile: "mobile",
  tablet: "tablet",
  desktop: "desktop"
}

const ORIENTATION = {
  portrait: "portrait",
  landscape: "landscape"
}

const QUERY_MOBILE = `(max-width: ${Res._512}px)`
const QUERY_TABLET = `(min-width: ${Res._512}px) and (max-width: ${Res._768}px)`
const QUERY_DESKTOP = `(min-width: ${Res._768}px)`
const QUERY_PORTRAIT = `(orientation: portrait)`

const Context = createContext({
  device: DEVICE.mobile,
  orientation: ORIENTATION.portrait
})

// eslint-disable-next-line react/prop-types
const DeviceProvider = ({ children }) => {
  const [device, setDevice] = useState(DEVICE.mobile)
  const [orientation, setOrientation] = useState(ORIENTATION.portrait)

  useLayoutEffect(() => {
    const mobileMediaQuery = window.matchMedia(QUERY_MOBILE)
    const tabletMediaQuery = window.matchMedia(QUERY_TABLET)
    const desktopMediaQuery = window.matchMedia(QUERY_DESKTOP)
    const portraitMediaQuery = window.matchMedia(QUERY_PORTRAIT)

    const handleMobileChange = ({ matches }) => {
      if (matches) setDevice(DEVICE.mobile)
    }

    const handleTabletChange = ({ matches }) => {
      if (matches) setDevice(DEVICE.tablet)
    }

    const handleDesktopChange = ({ matches }) => {
      if (matches) setDevice(DEVICE.desktop)
    }

    const handlePortraitChange = ({ matches }) => {
      setOrientation(matches ? ORIENTATION.portrait : ORIENTATION.landscape)
    }

    mobileMediaQuery.addEventListener("change", handleMobileChange)
    tabletMediaQuery.addEventListener("change", handleTabletChange)
    desktopMediaQuery.addEventListener("change", handleDesktopChange)
    portraitMediaQuery.addEventListener("change", handlePortraitChange)

    if (mobileMediaQuery.matches) setDevice(DEVICE.mobile)
    if (tabletMediaQuery.matches) setDevice(DEVICE.tablet)
    if (desktopMediaQuery.matches) setDevice(DEVICE.desktop)

    setOrientation(portraitMediaQuery.matches ? ORIENTATION.portrait : ORIENTATION.landscape)

    return () => {
      mobileMediaQuery.removeEventListener("change", handleMobileChange)
      tabletMediaQuery.removeEventListener("change", handleTabletChange)
      desktopMediaQuery.removeEventListener("change", handleDesktopChange)
      portraitMediaQuery.removeEventListener("change", handlePortraitChange)
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      device,
      orientation
    }),
    [device, orientation]
  )

  return <Context.Provider value={contextValue}>{children}</Context.Provider>
}

export const onInitialClientRender = () => {
  document.body.classList.remove("loading")
}

/*
  GrowthBookProvider used to wrap the root here, which pulled
  @growthbook/growthbook and dom-mutator into app.js -- downloaded by all 219
  pages although only the 4 ABTestTemplate ones read a feature. Importing
  src/lib/growthbook also ran its module side effects everywhere: it sets
  window.growthbook and, on production hostnames, opens a streaming gb.init()
  connection to the GrowthBook CDN.

  The provider now lives inside ABTestTemplate, so the SDK is code-split into
  that template's chunk and never fetched on the other pages.
*/
export const wrapRootElement = ({ element }) => {
  return <DeviceProvider>{element}</DeviceProvider>
}
